// Work Order Service for API interactions
// Based on the API endpoints from the test file

import { apiEndpoints } from '../config/env'
import { apiService } from './apiService'

// Add interfaces for API responses
interface ApiWorkOrderResponse {
  workOrderID?: number
  clientName?: string
  propertyAddress?: string
  clientOrderNo?: string
  techOrderNo?: number | null
  inspectionType?: string
  inspectionTypeName?: string
  dueDate?: string
  scheduledDate?: string | null
  woScheduledDate?: string | null
  keyNo?: string | null
  keyPickup?: boolean
  requestedTime?: string | null
  woOrderNotes?: string | null
  clientNotes?: string | null
  clientType?: string
  packageDetails?: string
  packageDesc?: string
  propertyType?: string
  status?: string
  priority?: string
  technicianId?: number | null
  technicianName?: string
  statusID_FK?: number | null
  creationDate?: string | null
  eta?: string | null
  inspectionTime?: string | null
  onArrivalID?: number | null
  alarmStatusID?: number | null
  qrCodeID?: number | null
  safetySwitchReq?: boolean
  safetySwitchPass?: boolean
  safetySwitchLocationID?: number | null
  safetySwitchNotes?: string | null
  cordedBlindReq?: boolean
  totalBlinds?: number | null
  cleatsInstalled?: number | null
  warningTagsInstalled?: number | null
  nonCompliantBlinds?: number | null
  blindNotes?: string | null
  inspectionNotes?: string | null
  complianceNotes?: string | null
  upgradeCompletedByCSA?: boolean
  suspectedAsbestos?: boolean
  complianceStandard?: string | null
  statusID?: number | null
  alarmsRequired?: number | null
  noRooms?: number | null
  noLevels?: number | null
}

interface ApiContactResponse {
  clientPropertyTenantID?: number
  tenantName?: string
  tenantPhone?: string
  tenantEmail?: string | null
  leaseDateFrom?: string | null
  leaseDateTo?: string | null
  isCurrent?: boolean
}

interface ApiAlarmResponse {
  workOrderAlarmID?: number
  alarmTypeID?: number
  alarmType?: string
  sensorNo?: number
  expiryM?: string
  expiryY?: string
  alarmLocationID?: number
  alarmLocation?: string
  alarmLocationOther?: string | null
  alarmBrandID?: number
  alarmBrand?: string
  alarmBrandOther?: string | null
  db?: number | null
  batt?: string | null
  alarmRFRID?: number | null
  isExisting?: boolean
  installationID?: number | null
  replaceType?: string | null
}

interface ApiWorkOrderDetailsResponse {
  workOrder?: ApiWorkOrderResponse
  contacts?: ApiContactResponse[]
  existingAlarms?: ApiAlarmResponse[]
  newAlarms?: ApiAlarmResponse[]
}

interface ApiListResponse<T> {
  data?: T[]
}

interface ScheduledStop {
  order: number
  type: string
  address: string
  scheduledTime: string
  eta: string
  durationMinutes: number
  timeWindow: string
  workOrderId?: number
  keyNumber?: string
  description?: string
}

interface OptimizedSchedule {
  date: string
  technicianId: number
  stops: ScheduledStop[]
  totalDrivingMinutes: number
  totalWorkMinutes: number
  startTime: string
  endTime: string
}

interface ScheduledJob {
  workOrderId: number
  address: string
  scheduledTime: string
  estimatedDurationMinutes: number
  status: string
}

interface DayPlanningResult {
  date: string
  technicianId: number
  unscheduledJobsFound: number
  jobsScheduled: number
  scheduledJobs: ScheduledJob[]
  status: string
  message: string
}

interface SchedulingRequest {
  startDate: string
  endDate: string
  technicianIds?: number[]
  dryRun: boolean
}

interface SchedulingResult {
  success: boolean
  message: string
  totalJobsScheduled: number
  totalJobsFound: number
  unscheduledJobs: UnscheduledJobSummary[]
  scheduledJobs: ScheduledJobSummary[]
  generatedAt: string
  logText: string
}

interface LiveETAUpdateResult {
  success: boolean
  message?: string
  updatedAt?: string
}

interface ScheduledJobSummary {
  workOrderId: number
  clientName: string
  address: string
  scheduledDate: string
  dueDate: string
  technicianId: number
  technicianName: string
  techOrderNo: number
  estimatedMinutes: number
}

interface UnscheduledJobSummary {
  workOrderId: number
  clientName: string
  address: string
  dueDate: string
  reason: string
}

// SMS Interfaces
export interface SmsTemplateResponse {
  templateBody: string
}

export interface SendSmsRequest {
  workOrderId: number
  phoneNumber: string
  message: string
  smsType: string
}

export interface SendSmsResponse {
  success: boolean
  message: string
}

export interface SmsTypeOption {
  value: string
  label: string
  systemReference: string
}

export interface WorkOrder {
  workOrderID: number
  clientName: string
  propertyAddress: string
  clientOrderNo: string
  techOrderNo?: number | null
  inspectionType: string
  dueDate: string
  scheduledDate?: string | null
  keyNo?: string | null
  requestedTime?: string | null
  workOrderNotes?: string | null
  clientNotes?: string | null
  clientType: string
  packageDetails: string
  packageDesc?: string
  propertyType: string
  status: string
  priority: string
  technicianId?: number | null,
  keyPickup?: boolean
  eta?: string | null
}

export type DayDestinationType = 'KeyPickup' | 'KeyDropoff' | 'LunchBreak' | 'WorkOrder' | 'Totals'

export interface TechnicianDayDestination {
  technicianDayDestinationID: number
  technicianID_FK: number
  destinationDate: string
  destinationType: DayDestinationType
  destinationName: string
  address: string
  duration: number
  scheduledTime?: string | null
  completed?: boolean
  orderSequence?: number | null
  clientPropertyID_FK?: number | null
  workOrderID_FK?: number | null
  notes?: string | null
  latitude?: number | null
  longitude?: number | null
}

export interface TechnicianWorkOrderDetails extends WorkOrder {
  inspectionTime?: string
  onArrivalID?: number
  alarmStatusID?: number
  qrCodeID?: number
  safetySwitchReq?: boolean
  safetySwitchPass?: boolean
  safetySwitchLocationID?: number
  safetySwitchNotes?: string
  cordedBlindReq?: boolean
  totalBlinds?: number
  cleatsInstalled?: number
  warningTagsInstalled?: number
  nonCompliantBlinds?: number
  blindNotes?: string
  inspectionNotes?: string
  complianceNotes?: string
  upgradeCompletedByCSA?: boolean
  suspectedAsbestos?: boolean
  complianceStandard?: string
  statusID?: number
  alarmsRequired?: number
  noRooms?: number
  noLevels?: number
  newAlarms?: WorkOrderAlarm[]
  existingAlarms?: WorkOrderAlarm[]
}

export interface Contact {
  clientPropertyTenantID: number
  tenantName: string
  tenantPhone: string
  tenantEmail?: string
  leaseDateFrom?: string
  leaseDateTo?: string
  isCurrent: boolean
}

export interface WorkOrderAlarm {
  workOrderAlarmID: number
  alarmTypeID: number
  alarmType: string
  sensorNo: number
  expiryM: string
  expiryY: string
  alarmLocationID: number
  alarmLocation: string
  alarmLocationOther?: string
  alarmBrandID: number
  alarmBrand: string
  alarmBrandOther?: string
  db?: number
  batt?: string
  alarmRFRID?: number
  isExisting: boolean
  installationID?: number
  replaceType?: string
}

// Work Order Comment Update interfaces
export interface UpdateWorkOrderCommentRequest {
  workOrderId: number
  comment: string | null
}

export interface UpdateWorkOrderCommentResponse {
  success: boolean
  message: string
  workOrderId: number
  updatedComment: string | null
  updatedAt: string
}

class WorkOrderService {
  // Helper method to validate and sanitize parameters
  private validateTechnicianId(technicianId: number | null | undefined): number {
    if (!technicianId || technicianId <= 0) {
      throw new Error('Invalid technician ID')
    }
    return technicianId
  }

  private validateWorkOrderId(workOrderId: number | null | undefined): number {
    if (!workOrderId || workOrderId <= 0) {
      throw new Error('Invalid work order ID')
    }
    return workOrderId
  }

  private validateDate(date: string | null | undefined): string {
    if (!date) {
      throw new Error('Date is required')
    }
    // Ensure date is in correct format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (!dateRegex.test(date)) {
      throw new Error('Date must be in YYYY-MM-DD format')
    }
    return date
  }

  // Get technician's assigned work orders
  async getTechnicianWorkOrders(technicianId: number): Promise<WorkOrder[]> {
    try {
      const validTechnicianId = this.validateTechnicianId(technicianId)
      const data: ApiListResponse<ApiWorkOrderResponse> | ApiWorkOrderResponse[] = await apiService.get(apiEndpoints.workOrders.technician(validTechnicianId))
      
      const workOrders = Array.isArray(data) ? data : (data.data || [])
      
      // Handle null values in the response
      return workOrders.map(this.mapApiResponseToWorkOrder)
    } catch (error) {
      throw error
    }
  }

  // Helper method to sanitize work order details
  private sanitizeWorkOrderDetails(wo: ApiWorkOrderResponse): TechnicianWorkOrderDetails {
    return {
      workOrderID: wo.workOrderID || 0,
      clientName: wo.clientName || 'Unknown Client',
      propertyAddress: wo.propertyAddress || 'No Address',
      clientOrderNo: wo.clientOrderNo || '',
      inspectionType: wo.inspectionType || 'General Inspection',
      dueDate: wo.dueDate || new Date().toISOString(),
      scheduledDate: wo.scheduledDate || null,
      keyNo: wo.keyNo || null,
      requestedTime: wo.requestedTime || null,
      workOrderNotes: wo.woOrderNotes || null,
      clientNotes: wo.clientNotes || null,
      clientType: wo.clientType || 'Unknown',
      packageDetails: wo.packageDetails || 'Standard Package',
      packageDesc: wo.packageDesc || undefined,
      propertyType: wo.propertyType || 'Residential',
      status: wo.status || 'Scheduled',
      priority: wo.priority || 'Medium',
      technicianId: wo.technicianId || null,
      eta: wo.eta || null,
      safetySwitchReq: wo.safetySwitchReq || false,
      safetySwitchPass: wo.safetySwitchPass || false,
      cordedBlindReq: wo.cordedBlindReq || false,
      upgradeCompletedByCSA: wo.upgradeCompletedByCSA || false,
      suspectedAsbestos: wo.suspectedAsbestos || false,
    }
  }

  // Helper method to sanitize contact data
  private sanitizeContact = (contact: ApiContactResponse): Contact => {
    return {
      clientPropertyTenantID: contact.clientPropertyTenantID || 0,
      tenantName: contact.tenantName || 'Unknown Tenant',
      tenantPhone: contact.tenantPhone || 'No Phone',
      isCurrent: contact.isCurrent || false
    }
  }

  // Helper method to convert null to undefined
  private nullToUndefined<T>(value: T | null): T | undefined {
    return value === null ? undefined : value
  }

  // Helper method to sanitize alarm data
  private sanitizeAlarm = (alarm: ApiAlarmResponse): WorkOrderAlarm => {
    return {
      workOrderAlarmID: alarm.workOrderAlarmID || 0,
      alarmTypeID: alarm.alarmTypeID || 0,
      alarmType: alarm.alarmType || 'Unknown Type',
      sensorNo: alarm.sensorNo || 0,
      expiryM: alarm.expiryM || '',
      expiryY: alarm.expiryY || '',
      alarmLocationID: alarm.alarmLocationID || 0,
      alarmLocation: alarm.alarmLocation || 'Unknown Location',
      alarmLocationOther: this.nullToUndefined(alarm.alarmLocationOther),
      alarmBrandID: alarm.alarmBrandID || 0,
      alarmBrand: alarm.alarmBrand || 'Unknown Brand',
      alarmBrandOther: this.nullToUndefined(alarm.alarmBrandOther),
      db: this.nullToUndefined(alarm.db),
      batt: this.nullToUndefined(alarm.batt),
      alarmRFRID: this.nullToUndefined(alarm.alarmRFRID),
      isExisting: alarm.isExisting || false,
      installationID: this.nullToUndefined(alarm.installationID),
      replaceType: this.nullToUndefined(alarm.replaceType)
    }
  }

  // Update technician work order details
  async updateTechnicianWorkOrder(
    workOrderId: number, 
    updateData: Partial<TechnicianWorkOrderDetails>
  ): Promise<void> {
    try {
      const validWorkOrderId = this.validateWorkOrderId(workOrderId)
      
      // Remove null/undefined values to prevent SQL null issues
      const sanitizedData = this.removeNullValues(updateData)
      
      // Add WorkOrderID to the request body as required by the API
      const requestData = {
        WorkOrderID: validWorkOrderId,
        ...sanitizedData
      }
      
      await apiService.put(apiEndpoints.workOrders.update(validWorkOrderId), requestData)
    } catch (error) {
      throw error
    }
  }

  // Update work order notes specifically
  async updateWorkOrderNotes(workOrderId: number, notes: string): Promise<void> {
    try {
      const validWorkOrderId = this.validateWorkOrderId(workOrderId)
      
      await apiService.put(`${apiEndpoints.workOrders.update(validWorkOrderId).replace('/technician', '')}`, {
        WorkOrderID: validWorkOrderId,
        WOOrderNotes: notes
      })
    } catch (error) {
      throw error
    }
  }

  // Helper method to remove null/undefined values
  private removeNullValues(obj: Record<string, unknown>): Record<string, unknown> {
    const cleaned: Record<string, unknown> = {}
    Object.keys(obj).forEach(key => {
      if (obj[key] !== null && obj[key] !== undefined) {
        cleaned[key] = obj[key]
      }
    })
    return cleaned
  }

  // Get technician runsheet for a specific date
  async getTechnicianRunsheet(technicianId: number, date: string): Promise<WorkOrder[]> {
    try {
      const validTechnicianId = this.validateTechnicianId(technicianId)
      const validDate = this.validateDate(date)
      
      const result: ApiListResponse<ApiWorkOrderResponse> | ApiWorkOrderResponse[] = await apiService.get(`${apiEndpoints.runsheet}?technicianId=${validTechnicianId}&date=${validDate}`)
      
      const workOrders = Array.isArray(result) ? result : (result.data || [])
      
      return workOrders.map(this.mapApiResponseToWorkOrder)
    } catch (error) {
      return [] // Return empty array on error to prevent crashes
    }
  }

  // Get lookup data for dropdowns
  async getLookupData(): Promise<{
    onArrival: Array<{ id: number; name: string }>
    alarmStatus: Array<{ id: number; name: string }>
    qrCode: Array<{ id: number; name: string }>
    safetyLocation: Array<{ id: number; name: string }>
    unableToTest: Array<{ id: number; name: string }>
    alarmTypes: Array<{ id: number; name: string }>
    alarmLocations: Array<{ id: number; name: string }>
    alarmBrands: Array<{ id: number; name: string }>
    alarmRFR: Array<{ id: number; name: string }>
    compliance: Array<{ id: number; name: string }>
    status: Array<{ id: number; name: string }>
  }> {
    return apiService.get(apiEndpoints.lookup.workOrder)
  }

  // Add existing alarm
  async addExistingAlarm(workOrderId: number, alarmData: Partial<WorkOrderAlarm>): Promise<WorkOrderAlarm> {
    alarmData.replaceType = 'Existing';
    return apiService.post(apiEndpoints.alarms.addExisting(workOrderId), alarmData)
  }

  // Add new alarm
  async addNewAlarm(workOrderId: number, alarmData: Partial<WorkOrderAlarm>): Promise<WorkOrderAlarm> {
    alarmData.replaceType = 'New';
    return apiService.post(apiEndpoints.alarms.addNew(workOrderId), alarmData)
  }

  // Update alarm
  async updateAlarm(alarmId: number, alarmData: Partial<WorkOrderAlarm>): Promise<void> {
    await apiService.put(apiEndpoints.alarms.update(alarmId), alarmData)
  }

  // Delete alarm
  async deleteAlarm(alarmId: number): Promise<void> {
    await apiService.delete(apiEndpoints.alarms.delete(alarmId))
  }

  // Upload photo for alarm
  async uploadAlarmPhoto(alarmId: number, file: File): Promise<void> {
    
    // check if file is an image
    
    const formData = new FormData()
    formData.append('file', file)
    
    await apiService.uploadFile(apiEndpoints.alarms.uploadPhoto(alarmId), formData)
  }

  // Upload work order photo
  async uploadWorkOrderPhoto(workOrderId: number, file: File): Promise<void> {
    const formData = new FormData()
    formData.append('file', file)
    
    await apiService.uploadFile(apiEndpoints.files.uploadWorkOrderPhoto(workOrderId), formData)
  }

  // Get dashboard technicians data
  async getDashboardTechnicians(): Promise<Array<{
    techUserID_FK: number
    techname: string
    allJobs: number
    totalTime: number
    bookedJobs: number
    bookedTime: number
    checkoutTime?: string
    isCheckedOut: boolean
  }>> {
    const result: { data?: Array<{
      techUserID_FK: number
      techname: string
      allJobs: number
      totalTime: number
      bookedJobs: number
      bookedTime: number
      checkoutTime?: string
      isCheckedOut: boolean
    }> } = await apiService.get(`${apiEndpoints.dashboard}/technicians`)
    return result.data || []
  }

  // Note: Message functionality has been moved to messageService.ts

  // Checkout/checkin technician (mock for now)
  async checkoutTechnician(technicianId: number, isCheckout: boolean): Promise<void> {
    // Mock implementation - replace with actual API call when available
  }

  // Get JSA link for technician
  getJSALink(technicianId: number, workOrderId: number): string {
    return `${apiEndpoints.jsa}/technician/${technicianId}?workOrderId=${workOrderId}`
  }

  // Import alarms from previous work order
  async importAlarms(workOrderId: number, propertyId: number): Promise<WorkOrderAlarm[]> {
    return apiService.post(apiEndpoints.importAlarms(), { 
      workOrderId,
      propertyId 
    })
  }

  // Helper method to map status strings to status IDs
  private getStatusId(status: string): number {
    const statusMap: { [key: string]: number } = {
      'all': 0, // Special case for "all" - we'll handle this in the API call
      'booked': 1,
      'completed': 2,
      'cancelled': 3,
      'unabletocomplete': 4,
      'unable to complete': 4
    }
    return statusMap[status.toLowerCase()] || 1 // Default to scheduled
  }

  // Get filtered work orders using the new filter endpoint
  async getFilteredWorkOrders(
    technicianId: number,
    selectedDate: string,
    statusFilter: string = 'all'
  ): Promise<WorkOrder[]> {
    try {
      const validTechnicianId = this.validateTechnicianId(technicianId)
      const validDate = this.validateDate(selectedDate)
      
      // If "all" status is selected, we need to fetch all status types
      if (statusFilter === 'all') {
        const allStatusIds = [1, 2, 3, 4] // scheduled, in progress, completed, cancelled
        
        // Make parallel requests for all statuses
        const promises = allStatusIds.map(async (statusId) => {
          const url = new URL(apiEndpoints.workOrders.filter)
          url.searchParams.append('technicianId', validTechnicianId.toString())
          url.searchParams.append('date', validDate)
          url.searchParams.append('statusId', statusId.toString())

          try {
            const data: ApiWorkOrderResponse[] = await apiService.get(url.toString())
            return data || []
          } catch (error) {
            return []
          }
        })
        
        const results = await Promise.all(promises)
        const flattenedResults = results.flat()
        
        // Remove duplicates based on workOrderID
        const uniqueWorkOrders = flattenedResults.filter((wo, index, self) => 
          index === self.findIndex(w => w.workOrderID === wo.workOrderID)
        )
        
        return uniqueWorkOrders.map(this.mapApiResponseToWorkOrder)
      } else {
        // Single status filter
        const statusId = this.getStatusId(statusFilter)
        
        const url = new URL(apiEndpoints.workOrders.filter)
        url.searchParams.append('technicianId', validTechnicianId.toString())
        url.searchParams.append('date', validDate)
        url.searchParams.append('statusId', statusId.toString())

        const data: ApiWorkOrderResponse[] = await apiService.get(url.toString())
        return (data || []).map(this.mapApiResponseToWorkOrder)
      }
    } catch (error) {
      throw error
    }
  }

  // Helper method to map API response to WorkOrder interface
  private mapApiResponseToWorkOrder = (wo: ApiWorkOrderResponse): WorkOrder => ({
    workOrderID: wo.workOrderID || 0,
    clientName: wo.clientName || 'Unknown Client',
    propertyAddress: wo.propertyAddress || 'No Address',
    clientOrderNo: wo.clientOrderNo || '',
    techOrderNo: wo.techOrderNo || null,
    inspectionType: wo.inspectionTypeName || wo.inspectionType || 'General Inspection',
    dueDate: wo.dueDate || new Date().toISOString(),
    scheduledDate: wo.woScheduledDate || wo.scheduledDate || null,
    keyNo: wo.keyNo || null,
    requestedTime: wo.requestedTime || null,
          workOrderNotes: wo.woOrderNotes || null,
    clientNotes: wo.clientNotes || null,
    clientType: wo.clientType || 'Unknown',
    packageDetails: wo.packageDetails || 'Standard Package',
    packageDesc: wo.packageDesc || undefined,
    propertyType: wo.propertyType || 'Residential',
    status: wo.status || 'Scheduled',
    priority: wo.priority || 'Medium',
    technicianId: wo.technicianId || null,
    eta: wo.eta || wo.ETA || null,
    keyPickup: wo.keyPickup || false
  })

  // Update work order comment
  async updateWorkOrderComment(workOrderId: number, comment: string | null): Promise<UpdateWorkOrderCommentResponse> {
    try {
      const validWorkOrderId = this.validateWorkOrderId(workOrderId)
      
      const requestBody: UpdateWorkOrderCommentRequest = {
        workOrderId: validWorkOrderId,
        comment: comment
      }

      return await apiService.patch(`${apiEndpoints.workOrders.base}/comment`, requestBody)
    } catch (error) {
      throw error
    }
  }

  // Update tech order number
  async updateTechOrderNo(workOrderId: number, techOrderNo: number | null): Promise<void> {
    try {
      const validWorkOrderId = this.validateWorkOrderId(workOrderId)

      const requestBody = {
        WorkOrderId: validWorkOrderId,
        TechOrderNo: techOrderNo
      }

      await apiService.patch(`${apiEndpoints.workOrders.base}/techOrderNo`, requestBody)
    } catch (error) {
      throw error
    }
  }

  // Update requested time
  async updateRequestedTime(workOrderId: number, requestedTime: string | null): Promise<void> {
    try {
      const validWorkOrderId = this.validateWorkOrderId(workOrderId)

      // Convert time string to DateTime if needed
      let dateTimeValue: string | null = null
      if (requestedTime) {
        if (requestedTime.includes('T')) {
          // Already a full DateTime
          dateTimeValue = requestedTime
        } else if (requestedTime.match(/^\d{2}:\d{2}(:\d{2})?$/)) {
          // Just a time string, combine with today's date
          const today = new Date().toISOString().split('T')[0]
          dateTimeValue = `${today}T${requestedTime}:00`
        } else {
          dateTimeValue = requestedTime
        }
      }

      const requestBody = {
        WorkOrderId: validWorkOrderId,
        RequestedTime: dateTimeValue
      }

      await apiService.patch(`${apiEndpoints.workOrders.base}/requestedTime`, requestBody)
    } catch (error) {
      throw error
    }
  }


  // Optimize running order for technician
  async optimizeRunningOrder(technicianId: number, date: string): Promise<OptimizedSchedule> {
    try {
      const validTechnicianId = this.validateTechnicianId(technicianId)
      const validDate = this.validateDate(date)
      
      const url = `${apiEndpoints.base}/api/RunningOrder/optimize?technicianId=${validTechnicianId}&date=${validDate}`

      return await apiService.post(url, {})
    } catch (error) {
      throw error
    }
  }

  // Get technician day destinations for a date
  async getDayDestinations(technicianId: number, date: string): Promise<TechnicianDayDestination[]> {
    const validTechnicianId = this.validateTechnicianId(technicianId)
    const validDate = this.validateDate(date)
    const url = apiEndpoints.dayDestinations.list(validTechnicianId, validDate)
    try {
      const data = await apiService.get<TechnicianDayDestination[]>(url)
      return data || []
    } catch (err) {
      return []
    }
  }

  // Update combined ordering for work orders and destinations
  async updateCombinedOrder(
    technicianId: number,
    date: string,
    items: Array<{ type: 'WorkOrder' | 'Destination'; id: number; order: number }>
  ): Promise<void> {
    const validTechnicianId = this.validateTechnicianId(technicianId)
    const validDate = this.validateDate(date)
    const payload = { technicianId: validTechnicianId, date: validDate, items }
    await apiService.post(apiEndpoints.dayDestinations.updateOrder, payload)
  }

  // Set destination completed
  async setDestinationCompleted(destinationId: number, completed: boolean): Promise<void> {
    const payload = { destinationId, completed }
    await apiService.post(apiEndpoints.dayDestinations.setCompleted, payload)
  }

  // Write-back destination lat/lng after frontend geocoding
  async updateDestinationLocation(destinationId: number, latitude: number, longitude: number): Promise<void> {
    const payload = { destinationId, latitude, longitude }
    await apiService.post(apiEndpoints.dayDestinations.updateLocation, payload)
  }

  // Update live ETA for technician for the specified date
  async updateTechnicianETA(technicianId: number, date: string): Promise<LiveETAUpdateResult> {
    try {
      const validTechnicianId = this.validateTechnicianId(technicianId)
      const validDate = this.validateDate(date)

      const url = `${apiEndpoints.base}/api/LiveETA/update-eta/${validTechnicianId}?date=${validDate}`

      return await apiService.post(url, {})
    } catch (error) {
      throw error
    }
  }

  // Execute day planning for technician
  async executeDayPlanning(technicianId: number, date: string): Promise<DayPlanningResult> {
    try {
      const validTechnicianId = this.validateTechnicianId(technicianId)
      const validDate = this.validateDate(date)
      
      const url = `${apiEndpoints.base}/api/DayPlanning/execute?technicianId=${validTechnicianId}&date=${validDate}`

      return await apiService.post(url, {})
    } catch (error) {
      throw error
    }
  }

  // Execute scheduling for date range
  async executeScheduling(request: SchedulingRequest): Promise<SchedulingResult> {
    try {
      const url = `${apiEndpoints.base}/api/Scheduling/generate`
      
      return await apiService.post(url, request)
    } catch (error) {
      throw error
    }
  }

  // Execute scheduling with query parameters (alternative method)
  async executeSchedulingQuery(startDate: string, endDate: string, technicianIds?: number[]): Promise<SchedulingResult> {
    try {
      const validStartDate = this.validateDate(startDate)
      const validEndDate = this.validateDate(endDate)

      let url = `${apiEndpoints.base}/api/Scheduling/generate?startDate=${validStartDate}&endDate=${validEndDate}`

      if (technicianIds && technicianIds.length > 0) {
        const validTechnicianIds = technicianIds.map(id => this.validateTechnicianId(id))
        url += `&technicianIds=${validTechnicianIds.join(',')}`
      }

      return await apiService.get(url)
    } catch (error) {
      throw error
    }
  }

  // Get available SMS types
  async getSmsTypes(): Promise<SmsTypeOption[]> {
    try {
      return await apiService.get(`${apiEndpoints.base}/api/Sms/types`)
    } catch (error) {
      throw error
    }
  }

  // Get SMS template for specific type
  async getSmsTemplate(smsType: string): Promise<SmsTemplateResponse> {
    try {
      return await apiService.get(`${apiEndpoints.base}/api/Sms/template/${smsType}`)
    } catch (error) {
      throw error
    }
  }

  // Send SMS
  async sendSms(workOrderId: number, phoneNumber: string, message: string, smsType: string): Promise<SendSmsResponse> {
    try {
      const validWorkOrderId = this.validateWorkOrderId(workOrderId)

      const requestBody: SendSmsRequest = {
        workOrderId: validWorkOrderId,
        phoneNumber,
        message,
        smsType
      }

      return await apiService.post(`${apiEndpoints.base}/api/Sms/send`, requestBody)
    } catch (error) {
      throw error
    }
  }
}

export const workOrderService = new WorkOrderService()
export type { OptimizedSchedule, ScheduledStop, SchedulingRequest, SchedulingResult, ScheduledJobSummary, UnscheduledJobSummary, SmsTemplateResponse, SendSmsRequest, SendSmsResponse, SmsTypeOption }